'use client';
import React, { Dispatch, FunctionComponent, SetStateAction, useEffect, useMemo } from 'react';
import { useResponsive } from '../../../contexts/ResponsiveContext';
import PageContainer from '../tableComponents/PageContainer';
import { Box, Divider, Typography } from '@mui/material';
import VerticalContainer from '../../common/VerticalContainer';
import ProductCard from './ProductCard';
import { BasePartParameterValue } from '../../../schemas/BasePartParameterValue';
import { getParameterFormattedValue } from '../../../utils/ParameterRelatedFunction';
import CustomPagination from '../../common/CustomPagination';
import { DetectedUniquePart } from '../../../schemas/DetectedUniquePart';
import { ProductsContent } from '../../../graphql/pimcore/modules/siteContent/types/ProductsContent';
import { formatCountIcuString } from '../../../utils/formatCount';
import { useLocale } from '../../../contexts/LocaleProvider';
import { useLazyGetOctoMultiPartQuery } from '../../../state/multipartSlice';
import { OctoPartRequest } from '../../../schemas/OctoPartRequest';

interface MobileProductDisplayProps {
    partCount: number;
    basePartResults: BasePartParameterValue[];
    detectedUniqueParts: DetectedUniquePart[] | undefined;
    page: number;
    setPage: Dispatch<SetStateAction<number>>;
    rowsPerPage: number;
    contentTree: ProductsContent;
}

const MobileProductDisplay: FunctionComponent<MobileProductDisplayProps> = (props) => {
    const { responsiveWidth } = useResponsive();
    const { locale } = useLocale();

    const handleChangePage = async (event: unknown, newPage: number) => {
        props.setPage(newPage);
    };

    const showUnique =
        props.basePartResults.length === 0 && (props.detectedUniqueParts?.length ?? 0) > 0;

    // 只查「目前頁面」要顯示的清單，避免一次送太多
    const pagedBaseParts = useMemo(() => {
        const offset = props.page * props.rowsPerPage;
        return props.basePartResults.slice(offset, offset + props.rowsPerPage);
    }, [props.basePartResults, props.page, props.rowsPerPage]);

    const pagedUniqueParts = useMemo(() => {
        const offset = props.page * props.rowsPerPage;
        return (props.detectedUniqueParts ?? []).slice(offset, offset + props.rowsPerPage);
    }, [props.detectedUniqueParts, props.page, props.rowsPerPage]);

    // 準備 Octopart 查詢 payload
    const octoRequests: OctoPartRequest[] = useMemo(() => {
        if (showUnique) {
            return pagedUniqueParts.map((dup) => ({
                uniquePn: dup.uniquePn, // unique 用 uniquePn
                aliases: dup.aliases ?? null,
            }));
        }
        return pagedBaseParts.map((bp) => ({
            uniquePn: bp.displayPn, // basePart 以 displayPn 當查詢鍵
            aliases: bp.alias ? [{ displayable: true, value: bp.alias }] : null,
        }));
    }, [showUnique, pagedBaseParts, pagedUniqueParts]);

    const [triggerOctoQuery, { data: octoData }] = useLazyGetOctoMultiPartQuery();

    // 清單變動就查一次 Octopart
    useEffect(() => {
        if (octoRequests.length > 0) {
            triggerOctoQuery(octoRequests);
        }
    }, [octoRequests, triggerOctoQuery]);

    // 把回傳換成 { pn -> 總庫存 } 對照表
    const stockMap = useMemo(() => {
        const map: Record<string, number> = {};
        (octoData ?? []).forEach((item) => {
            const total = item.filteredResult?.reduce((acc, p) => acc + p.quantity, 0) ?? 0;
            map[item.pn] = total; // item.pn 會是 uniquePn
        });
        return map;
    }, [octoData]);

    return (
        <VerticalContainer>
            <PageContainer
                containerName={'responsive-partcount'}
                sx={{ backgroundColor: 'white', paddingBottom: 0 }}
                innerSx={{ width: responsiveWidth, backgroundColor: 'white' }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        textAlign: 'center',
                        fontWeight: 500,
                        fontSize: '20px',
                        fontFamily: 'Poppins',
                    }}
                    data-testid={'mobile_partcount_zone'}
                >
                    {formatCountIcuString(
                        props.contentTree.filters.labels.resultCountCompact.content,
                        props.partCount,
                        locale
                    )}
                </Typography>
            </PageContainer>
            <PageContainer
                containerName={'responsive-divider'}
                sx={{ backgroundColor: 'white', paddingTop: 0 }}
                innerSx={{ width: responsiveWidth, backgroundColor: 'white' }}
            >
                {/* 橫向 Divider */}
                <Divider
                    orientation="horizontal"
                    flexItem
                    sx={{
                        borderColor: '#E5673E',
                        width: responsiveWidth,
                    }}
                />
            </PageContainer>

            {showUnique
                ? props.detectedUniqueParts?.map((uniquePart, index) => (
                      <ProductCard
                          key={uniquePart?.displayPn ?? index}
                          sx={{
                              backgroundColor: index % 2 === 0 ? 'white' : '#FDF5F0',
                          }}
                          basePn={uniquePart?.displayPn}
                          alias={uniquePart.aliases.find((a) => a.displayable)?.value ?? undefined}
                          productImageUrl={uniquePart?.imageUrl ?? ''}
                          stock={stockMap[uniquePart.uniquePn] ?? Number(uniquePart.stock ?? 0)}
                          description={
                              uniquePart.parameterValues.find((pv) => pv.parameterId === 14)
                                  ?.value ?? ''
                          }
                          specsheetSuppression={
                              uniquePart.parameterValues.find((pv) => pv.parameterId === 489)
                                  ?.value === 'true'
                                  ? true
                                  : undefined
                          }
                          dataSheet={
                              uniquePart.parameterValues.find((pv) => pv.parameterId === 296)
                                  ?.value ?? undefined
                          }
                          contentTree={props.contentTree}
                          locale={locale}
                      />
                  ))
                : props.basePartResults.map((part, index) => (
                      <ProductCard
                          key={`base-${index}`}
                          sx={{ backgroundColor: index % 2 === 0 ? 'white' : '#FDF5F0' }}
                          basePn={part.basePn}
                          alias={part.alias ?? undefined}
                          productImageUrl={part.imagePath ?? ''}
                          stock={Number(getParameterFormattedValue(part.parameterValues, 'Stock'))}
                          description={part.description ?? ''}
                          specsheetSuppression={part.specsheetSuppression ?? undefined}
                          dataSheet={part.dataSheet ?? undefined}
                          contentTree={props.contentTree}
                          locale={locale}
                      />
                  ))}

            <Box sx={{ paddingTop: '1rem', paddingBottom: '1rem', backgroundColor: 'white' }}>
                <CustomPagination
                    count={
                        props.detectedUniqueParts
                            ? props.detectedUniqueParts.length
                            : props.partCount
                    }
                    page={props.page}
                    rowsPerPage={props.rowsPerPage}
                    onPageChange={handleChangePage}
                />
            </Box>
        </VerticalContainer>
    );
};

export default MobileProductDisplay;
